import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Tooltip
} from '@chain-zen/ui'
import { useState } from 'react'
import { toast } from 'sonner'

import { ENABLE_PRIORITY1_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import { useMutationWithToast } from '../../../hooks/use-mutation-with-toast'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type TPriority1AppActionEnum = 'GenerateApiKey'

type TEnablePriority1AppInput = {
  action: TPriority1AppActionEnum
  appType: AppTypeEnum
}

export function Priority1Modal({ isOpen, onClose, appType, app, setModalEditable }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const [generatedApiKey, setGeneratedApiKey] = useState('')

  const [enablePriority1App, { loading }] = useMutation<
    { enablePriority1App: { appEnabled: boolean; chaineAPIToken?: string } },
    TEnablePriority1AppInput
  >(ENABLE_PRIORITY1_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    refetchQueries: [GET_APPS]
  })

  const executeWithToast = useMutationWithToast<
    { enablePriority1App: { appEnabled: boolean; chaineAPIToken?: string } },
    TEnablePriority1AppInput
  >({
    mutationFn: enablePriority1App,
    onError: (err) => setFormError(err.message)
  })

  const handleClose = () => {
    setFormError('')
    setGeneratedApiKey('')
    setModalEditable?.(false)
    onClose()
  }

  const handleCreateAPIKey = async () => {
    setFormError('')

    try {
      const result = await executeWithToast({
        loadingMessage: 'Generating...',
        successMessage: 'API key is ready.',
        variables: {
          action: 'GenerateApiKey',
          appType: appType as AppTypeEnum
        }
      })

      const apiKey = result?.data?.enablePriority1App?.chaineAPIToken
      if (apiKey) {
        setGeneratedApiKey(apiKey)
      }
    } catch {
      // error handled by onError callback
    }
  }

  const displayedApiKey = generatedApiKey || app?.workspaceApp?.appCredentials?.chaineAPIToken
  const isConfigured = !!displayedApiKey

  const getDialogTitle = (): string => {
    if (isConfigured) return 'Priority1 is configured for your workspace'
    return 'Enable Priority1 for Chain'
  }

  const getDialogDescription = (): string => {
    if (isConfigured) return 'Your API key is ready. Provide it to Priority1 to authenticate requests to Chain.'
    return 'Generate an API key for Priority1 to use when sending data to Chain.'
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        {!!formError && <Alert variant="error" description={formError} />}

        <div className="space-y-3">
          <p className="text-sm font-medium">Chain API Key</p>

          {displayedApiKey ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Provide this API key to Priority1 to authenticate requests to Chain.
              </p>
              <div className="rounded-md border bg-muted px-3 py-2 font-mono text-sm break-all">{displayedApiKey}</div>
              <div className="flex justify-end">
                <Tooltip content="Copy to clipboard">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(displayedApiKey)
                      toast.success('API key copied to clipboard!')
                    }}
                  >
                    Copy
                  </Button>
                </Tooltip>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Generate an API key for Priority1 to use when sending data to Chain.
              </p>
              <Button type="button" variant="outline" onClick={handleCreateAPIKey} disabled={loading}>
                {loading ? 'Generating' : 'Generate API Key'}
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
          {!!app?.docs && (
            <a
              href={app.docs}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              Read the integration guide
              <ExternalLink className="h-4 w-4" />
            </a>
          )}

          <Button variant="outline" onClick={handleClose} type="button">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
